import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  TextField as MuiTextField,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import FilterSelect from "../../UIKit/Select";
import FilterTextField from "../../UIKit/TextField";
import MuiAutoComplete from "../../../UIKit/MuiAutoComplete";
import FilterDatePicker from "../../UIKit/Datepicker";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import UploadFilterEmails from "../../UploadFilterEmails";
import {
  getOperatorsForField,
  getValueInputType,
  getValidOperatorForField,
  getNormalizedEmailValues,
  extractValidEmails,
  mergeUniqueEmails,
  parseEmailsFromCsvContent,
} from "../../utils";

export default function SubFilter({
  data,
  setValue,
  conditionId,
  subConditionId = 0,
  key,
  campaignUsers,
}) {
  const [step1, setStep1] = useState("");
  const [step2, setStep2] = useState("");
  const [selectedOperatorDisplay, setSelectedOperatorDisplay] = useState("");
  const [emailInputValue, setEmailInputValue] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // Effect to handle operator validation when field type changes
  useEffect(() => {
    const currentField = data?.category || step1;
    const currentOperator = data?.subOperator || step2;

    if (
      currentField &&
      currentOperator &&
      typeof currentField === "string" &&
      typeof currentOperator === "string"
    ) {
      try {
        const validOperator = getValidOperatorForField(
          currentOperator,
          currentField,
          campaignUsers,
        );
        if (validOperator && validOperator !== currentOperator) {
          setStep2(validOperator);
          setValue(conditionId, subConditionId, "subOperator", validOperator);
        }
      } catch (error) {
        console.warn("Error validating operator:", error);
        // Fallback to default operator
        setStep2("Is");
        setValue(conditionId, subConditionId, "subOperator", "Is");
      }
    }
  }, [
    data?.category,
    step1,
    campaignUsers,
    conditionId,
    subConditionId,
    setValue,
  ]);

  const step1Options = [
    { label: "Company", value: "organization" },
    { label: "Company ID", value: "organizationId" },
    { label: "Contact", value: "contact" },
    { label: "Country", value: "country" },
    { label: "City", value: "city" },
    { label: "Department", value: "department" },
    { label: "Email(s)", value: "Email" },
    { label: "Email Status", value: "emailStatus" },
    { label: "First Name", value: "First Name" },
    { label: "Full Name", value: "fullName" },
    { label: "Generic Name", value: "emailName" },
    { label: "Generic Company Name", value: "emailCompany" },
    { label: "Gender", value: "gender" },
    { label: "Industry", value: "industry" },
    { label: "Is Verified", value: "isVerified" },
    { label: "Job Title", value: "jobTitle" },
    { label: "Language", value: "language" },
    { label: "Last Name", value: "Last Name" },
    { label: "Lead Status", value: "leadStatus" },
    { label: "Company LinkedIn", value: "linkedIn" },
    { label: "Message Bucket", value: "messageBucket" },
    { label: "Person Id", value: "personId" },
    { label: "Delete Tag", value: "relationship" },
    { label: "Source", value: "source" },
    { label: "Lead Type", value: "specialMessageDaniel" },
    { label: "State", value: "state" },
    { label: "Tier Level", value: "tierLevel" },
    { label: "Tier Number", value: "tierNumber" },
    { label: "Tier Type", value: "tierType" },
    { label: "Sent From", value: "sentFromEmail" },
    { label: "Campaign Title", value: "Campaign Name" },
    { label: "Event", value: "Campaign Event" },
    { label: "Total Delivered", value: "totalSent" },
    { label: "Total Opens", value: "totalOpens" },
    { label: "Total Clicks", value: "totalClicks" },
    { label: "Total Campaigns", value: "totalCampaigns" },
    { label: "Step Number", value: "stepNumber" },
    { label: "Activity Date", value: "Activity Date" },
  ];

  const step1AlternateOptions = [
    { label: "Company", value: "organization" },
    { label: "Company ID", value: "organizationId" },
    { label: "Contact", value: "contact" },
    { label: "Country", value: "country" },
    { label: "City", value: "city" },
    { label: "Department", value: "department" },
    { label: "Email(s)", value: "Email" },
    { label: "Email Status", value: "emailStatus" },
    { label: "First Name", value: "First Name" },
    { label: "Last Name", value: "Last Name" },
    { label: "Full Name", value: "fullName" },
    { label: "Generic Name", value: "emailName" },
    { label: "Generic Company Name", value: "emailCompany" },
    { label: "Gender", value: "gender" },
    { label: "Industry", value: "industry" },
    { label: "Is Verified", value: "isVerified" },
    { label: "Job Title", value: "jobTitle" },
    { label: "Language", value: "language" },
    { label: "Lead Status", value: "leadStatus" },
    { label: "Company LinkedIn", value: "linkedIn" },
    { label: "Message Bucket", value: "messageBucket" },
    { label: "Person Id", value: "personId" },
    { label: "Delete Tag", value: "relationship" },
    { label: "Source", value: "source" },
    { label: "Lead Type", value: "specialMessageDaniel" },
    { label: "State", value: "state" },
    { label: "Tier Level", value: "tierLevel" },
    { label: "Tier Number", value: "tierNumber" },
    { label: "Tier Type", value: "tierType" },
    { label: "Sent From", value: "sentFromEmail" },
    { label: "Event", value: "Campaign Event" },
    { label: "Step Number", value: "stepNumber" },
    { label: "Activity Date", value: "Activity Date" },
  ];

  // Get operators based on current field type
  const getCurrentOperators = () => {
    const currentField = data?.category || step1;
    const operators = getOperatorsForField(currentField, campaignUsers);
    return Array.isArray(operators)
      ? operators
      : ["Is", "Is Not", "Is Empty", "Is Not Empty"];
  };

  const getOperatorsForDisplay = () => {
    const baseOperators = getCurrentOperators();
    const selectedCategory = step1 || data?.category;

    return selectedCategory === "Email" && baseOperators.includes("Like")
      ? [...baseOperators, "Contains"]
      : baseOperators;
  };

  const normalizeOperatorValue = (operator) => {
    return operator === "Contains" ? "Like" : operator;
  };

  const getOperatorDisplayValue = () => {
    const selectedCategory = step1 || data?.category;
    if (
      selectedCategory === "Email" &&
      data?.subOperator === "Like" &&
      selectedOperatorDisplay === "Contains"
    ) {
      return "Contains";
    }
    return data?.subOperator || "";
  };

  const currentCategory = step1 || data?.category;
  const currentOperator = data?.subOperator || step2;
  const isContainsMode =
    currentCategory === "Email" && currentOperator === "Like";
  const isEmailMultiValueInput =
    currentCategory === "Email" &&
    currentOperator !== "Is Empty" &&
    currentOperator !== "Is Not Empty";

  const extractEmailInputValues = (rawValue) => {
    const tokens = (rawValue || "")
      .split(/[\n,\s;]+/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (isContainsMode) {
      return tokens;
    }

    return tokens.filter((item) => extractValidEmails(item).length > 0);
  };

  const addEmailsToCondition = (emailsToAdd) => {
    if (!emailsToAdd || emailsToAdd.length === 0) return;
    const existingValues = getNormalizedEmailValues(data?.value);
    const deduped = mergeUniqueEmails(existingValues, emailsToAdd);
    setValue(conditionId, subConditionId, "value", deduped);
  };

  const handleCsvEmailUpload = async (file) => {
    if (!file) return;
    try {
      const content = await file.text();
      const matchedEmails = parseEmailsFromCsvContent(content);
      addEmailsToCondition(matchedEmails);
    } catch (error) {
      console.error("Failed to parse CSV emails:", error);
    } finally {
      setUploadModalOpen(false);
    }
  };

  useEffect(() => {
    const selectedCategory = step1 || data?.category;
    if (selectedCategory !== "Email" || data?.subOperator !== "Like") {
      setSelectedOperatorDisplay("");
    }
  }, [step1, data?.category, data?.subOperator]);

  const boolOPtions = [
    { label: "True", value: true },
    { label: "False", value: false },
  ];

  const emailStatus = ["Deliverable", "Undeliverable", "Risky", "Unknown"];
  const activityDateOptions = [
    "Past Year",
    "Past 6 Months",
    "Past 3 Months",
    "Past Month",
    "Past Week",
  ];

  // Domains used in campaign creation modal
  const senderDomains = [
    "marco@flavorwiki.biz",
    "marco@flavorwiki.net",
    "marco@flavorwiki.ch",
    "news@flavorwiki-newsletter.com",
  ];

  const campaignEventis = [
    { label: "Processed", value: "processed" },
    { label: "Dropped", value: "dropped" },
    { label: "Deferred", value: "deferred" },
    { label: "Blocked", value: "blocked" },
    { label: "Bounce", value: "bounce" },
    { label: "Delivered", value: "delivered" },
    { label: "Open", value: "open" },
    { label: "Click", value: "click" },
    { label: "Unsubscribe", value: "unsubscribe" },
    { label: "Spam report", value: "spamreport" },
  ];

  const options = [
    { label: "Is", value: "==" },
    { label: "Is Not", value: "!=" },
    { label: ">", value: ">" },
    { label: "<", value: "<" },
    { label: ">=", value: ">=" },
    { label: "<=", value: "<=" },
    { label: "Like", value: "Like" },
    { label: "Not Like", value: "unsubscribe" },
    { label: "Spam report", value: "spam report" },
  ];

  const userCampaignFilters = [
    "Currently in campaign",
    "In previous campaign",
    "Not in campaign",
  ];
  // console.log(data)
  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          gap: ".5rem",
          margin: "0 auto",
          alignItems: "center",
          flexWrap:
            step1 === "Campaign Time" || isEmailMultiValueInput
              ? "wrap"
              : "nowrap",
        }}
        key={key}
      >
        <FormControl fullWidth style={{ width: "10rem" }}>
          <MuiAutoComplete
            options={campaignUsers ? step1AlternateOptions : step1Options}
            value={
              data?.category
                ? (campaignUsers ? step1AlternateOptions : step1Options).find(
                    (option) => option.value === data.category,
                  )
                : null
            }
            size={"small"}
            fontSize={"12px"}
            width={"10rem"}
            label={"Category"}
            id={"campaign-filter-companies"}
            onChange={(newValue) => {
              const value = newValue ? newValue.value : "";
              setStep1(value);
              setValue(conditionId, subConditionId, "category", value);

              // Reset operator when field type changes to ensure compatibility
              if (
                value &&
                data?.subOperator &&
                typeof value === "string" &&
                typeof data.subOperator === "string"
              ) {
                try {
                  const validOperator = getValidOperatorForField(
                    data.subOperator,
                    value,
                    campaignUsers,
                  );
                  if (validOperator && validOperator !== data.subOperator) {
                    setStep2(validOperator);
                    setValue(
                      conditionId,
                      subConditionId,
                      "subOperator",
                      validOperator,
                    );
                  }
                } catch (error) {
                  console.warn(
                    "Error validating operator on field change:",
                    error,
                  );
                  // Fallback to default operator
                  setStep2("Is");
                  setValue(conditionId, subConditionId, "subOperator", "Is");
                }
              }
            }}
          />
        </FormControl>

        <FormControl fullWidth style={{ width: "5rem" }}>
          <FilterSelect
            options={getOperatorsForDisplay()}
            value={getOperatorDisplayValue()}
            size={"small"}
            fontSize={"12px"}
            width={"5rem"}
            label={"is/not"}
            id={"campaign-filter-companies"}
            onChange={(e) => {
              setSelectedOperatorDisplay(e.target.value);
              const normalizedOperator = normalizeOperatorValue(e.target.value);
              setValue(
                conditionId,
                subConditionId,
                "subOperator",
                normalizedOperator,
              );
              if (
                normalizedOperator === "Is Empty" ||
                normalizedOperator === "Is Not Empty"
              ) {
                setValue(conditionId, subConditionId, "value", "");
              }
              setStep2(normalizedOperator);
            }}
          />
        </FormControl>

        {isEmailMultiValueInput ? (
          <>
            <FormControl fullWidth sx={{ width: "18rem", maxWidth: "18rem" }}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={getNormalizedEmailValues(data?.value)}
                inputValue={emailInputValue}
                size="small"
                filterSelectedOptions
                renderTags={(tagValue, getTagProps) => {
                  if (!tagValue.length) return null;

                  return (
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "4px",
                        flex: "1 1 auto",
                        minWidth: 0,
                        maxHeight: "calc(88px - 32px)",
                        overflowY: "auto",
                        overflowX: "hidden",
                        alignContent: "flex-start",
                        alignItems: "flex-start",
                        pb: "4px",
                        boxSizing: "border-box",
                        "&::-webkit-scrollbar": {
                          width: "6px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#bbb",
                          borderRadius: "4px",
                        },
                      }}
                    >
                      {tagValue.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={key}
                            label={option}
                            size="small"
                            sx={{
                              height: 22,
                              maxWidth: "100%",
                              "& .MuiChip-label": {
                                fontSize: "11px",
                                px: 0.75,
                              },
                            }}
                            {...tagProps}
                          />
                        );
                      })}
                    </Box>
                  );
                }}
                onChange={(event, newValue) => {
                  const normalizedValues = (newValue || [])
                    .flatMap((item) =>
                      extractEmailInputValues(
                        typeof item === "string" ? item : String(item),
                      ),
                    )
                    .filter(Boolean);

                  const deduped = mergeUniqueEmails([], normalizedValues);
                  setValue(conditionId, subConditionId, "value", deduped);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  setEmailInputValue(newInputValue);

                  if (
                    reason === "input" &&
                    (newInputValue.includes(",") ||
                      newInputValue.includes("\n") ||
                      newInputValue.includes(";") ||
                      newInputValue.includes(" "))
                  ) {
                    const parsedEmails = extractEmailInputValues(newInputValue);
                    if (parsedEmails.length > 0) {
                      addEmailsToCondition(parsedEmails);
                      setEmailInputValue("");
                    }
                  }
                }}
                renderInput={(params) => {
                  const { InputProps, ...textFieldParams } = params;
                  const hasEmailTags =
                    getNormalizedEmailValues(data?.value).length > 0;

                  return (
                  <MuiTextField
                    {...textFieldParams}
                    placeholder="Type email and press Enter"
                    InputProps={{
                      ...InputProps,
                      sx: {
                        ...(InputProps?.sx || {}),
                        minHeight: "40px",
                        maxHeight: "88px",
                        height: "auto",
                        overflow: "hidden",
                        display: "flex",
                        flexWrap: "nowrap",
                        alignItems: "center",
                        padding: "8px 12px 11px",
                        boxSizing: "border-box",
                        gap: "4px",
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        minHeight: "40px",
                        maxHeight: "88px",
                        height: "auto",
                        overflow: "hidden",
                        alignItems: "center",
                        padding: "8px 12px 11px",
                        boxSizing: "border-box",
                      },
                      "& .MuiOutlinedInput-input, & .MuiAutocomplete-input": {
                        padding: "0 4px 2px !important",
                        margin: 0,
                        minWidth: hasEmailTags ? "80px" : "100%",
                        width: hasEmailTags ? "auto" : "100%",
                        flex: hasEmailTags ? "1 0 80px" : "1 1 auto",
                        height: "24px",
                        lineHeight: "24px",
                        alignSelf: "center",
                      },
                      "& .MuiOutlinedInput-input::placeholder": {
                        opacity: 1,
                      },
                    }}
                    onBlur={() => {
                      const parsedEmails =
                        extractEmailInputValues(emailInputValue);
                      if (parsedEmails.length > 0) {
                        addEmailsToCondition(parsedEmails);
                      }
                      setEmailInputValue("");
                    }}
                    onPaste={(e) => {
                      const pastedText = e.clipboardData.getData("text");
                      const parsedEmails = extractEmailInputValues(pastedText);

                      if (parsedEmails.length > 0) {
                        e.preventDefault();
                        addEmailsToCondition(parsedEmails);
                        setEmailInputValue("");
                      }
                    }}
                    inputProps={{
                      ...textFieldParams.inputProps,
                      style: { fontSize: "12px" },
                    }}
                  />
                  );
                }}
              />
            </FormControl>
            <Button
              startIcon={<UploadFileIcon />}
              color="primary"
              variant="contained"
              sx={{ color: "#F3F3F3", whiteSpace: "nowrap" }}
              onClick={() => setUploadModalOpen(true)}
            >
              Upload Csv
            </Button>
          </>
        ) : getValueInputType(
            step1 || data?.category,
            data?.subOperator || step2,
          ) === "select" &&
          (step1 === "emailStatus" ||
            (data && data.category === "emailStatus")) ? (
          <FormControl fullWidth style={{ width: "10rem" }}>
            <FilterSelect
              options={emailStatus}
              value={data?.value || ""}
              // defaultValue={null}
              size={"small"}
              width={"10rem"}
              fontSize={"12px"}
              id={"campaign-filter-email-status"}
              onChange={(e) => {
                setValue(conditionId, subConditionId, "value", e.target.value);
              }}
            />
          </FormControl>
        ) : getValueInputType(
            step1 || data?.category,
            data?.subOperator || step2,
          ) === "select" &&
          (step1 === "sentFromEmail" ||
            (data && data.category === "sentFromEmail")) ? (
          <FormControl fullWidth style={{ width: "14rem" }}>
            <FilterSelect
              options={senderDomains}
              value={data?.value || ""}
              size={"small"}
              width={"14rem"}
              fontSize={"12px"}
              label={"Sent From"}
              id={"campaign-filter-sent-from"}
              onChange={(e) => {
                setValue(conditionId, subConditionId, "value", e.target.value);
              }}
            />
          </FormControl>
        ) : getValueInputType(
            step1 || data?.category,
            data?.subOperator || step2,
          ) === "date" &&
          (step1 === "Activity Date" ||
            (data && data?.category === "Activity Date")) ? (
          <FormControl fullWidth style={{ width: "10rem" }}>
            <FilterSelect
              options={activityDateOptions}
              value={data?.value || ""}
              size={"small"}
              width={"10rem"}
              fontSize={"12px"}
              label={"Activity Date"}
              id={"campaign-filter-activity-date"}
              onChange={(e) => {
                setValue(conditionId, subConditionId, "value", e.target.value);
              }}
            />
          </FormControl>
        ) : getValueInputType(
            step1 || data?.category,
            data?.subOperator || step2,
          ) === "date" &&
          (step1 === "Campaign Start Date" ||
            step1 === "Campaign End Date" ||
            step1 === "Created At" ||
            (data && data?.category === "Campaign Start Date") ||
            data?.category === "Campaign End Date" ||
            data?.category === "Created At") ? (
          <FilterDatePicker
            label="Date"
            fontSize={"12px"}
            onChange={(e) => setValue(conditionId, subConditionId, "value", e)}
            value={data?.value || ""}
          />
        ) : getValueInputType(
            step1 || data?.category,
            data?.subOperator || step2,
          ) === "select" &&
          (step1 === "User Status" ||
            (data && data.category === "User Status")) ? (
          <FormControl fullWidth style={{ width: "10rem" }}>
            <FilterSelect
              options={userCampaignFilters}
              value={data?.value || ""}
              size={"small"}
              fontSize={"12px"}
              width={"10rem"}
              label={"User Status"}
              id={"campaign-filter-companies"}
              onChange={(e) => {
                setValue(conditionId, subConditionId, "value", e.target.value);
              }}
            />
          </FormControl>
        ) : getValueInputType(
            step1 || data?.category,
            data?.subOperator || step2,
          ) === "autocomplete" &&
          (step1 === "Campaign Event" ||
            (data && data.category === "Campaign Event")) ? (
          <FormControl fullWidth style={{ width: "10rem" }}>
            <MuiAutoComplete
              options={campaignEventis}
              value={
                campaignEventis.find(
                  (option) => option.value === data?.value,
                ) || null
              }
              getOptionLabel={(option) => option.label || ""}
              size="small"
              fontSize="12px"
              width="10rem"
              label="Campaign Event"
              id={"campaign-events"}
              onChange={(newValue) => {
                const value = newValue ? newValue.value : "";
                setValue(conditionId, subConditionId, "value", value);
              }}
            />
          </FormControl>
        ) : getValueInputType(
            step1 || data?.category,
            data?.subOperator || step2,
          ) === "autocomplete" &&
          (step1 === "blacklisted" ||
            step1 === "red flagged" ||
            step1 === "verified" ||
            data?.category === "Blacklisted" ||
            data?.category === "Red Flagged" ||
            data?.category === "Verified") ? (
          <FormControl fullWidth style={{ width: "10rem" }}>
            <MuiAutoComplete
              options={boolOPtions}
              value={data?.value}
              // defaultValue={true}
              size={"small"}
              fontSize={"12px"}
              width={"10rem"}
              label={"Value"}
              id={"campaign-events"}
              onChange={(e) => {
                setValue(conditionId, subConditionId, "value", e.value);
              }}
            />
          </FormControl>
        ) : getValueInputType(
            step1 || data?.category,
            data?.subOperator || step2,
          ) === "disabled" &&
          (step2 === "Is Empty" || data.subOperator === "Is Empty") ? (
          <FilterTextField
            size={"small"}
            value={"null"}
            defaultValue={"null"}
            width={"10rem"}
            fontSize={"12px"}
            disabled={true}
          />
        ) : getValueInputType(
            step1 || data?.category,
            data?.subOperator || step2,
          ) === "disabled" &&
          (step2 === "Is Not Empty" || data.subOperator === "Is Not Empty") ? (
          <FilterTextField
            size={"small"}
            value={"not null"}
            width={"10rem"}
            fontSize={"12px"}
            disabled={true}
          />
        ) : getValueInputType(
            step1 || data?.category,
            data?.subOperator || step2,
          ) === "number" ? (
          <FilterTextField
            type="number"
            size={"small"}
            value={data?.value || ""}
            width={"10rem"}
            fontSize={"12px"}
            onChange={(e) => {
              setValue(conditionId, subConditionId, "value", e.target.value);
            }}
          />
        ) : (
          <FilterTextField
            size={"small"}
            value={data?.value || ""}
            width={"10rem"}
            fontSize={"12px"}
            onChange={(e) => {
              setValue(conditionId, subConditionId, "value", e.target.value);
            }}
          />
        )}
      </Box>
      <UploadFilterEmails
        open={uploadModalOpen}
        setOpen={setUploadModalOpen}
        onUpload={handleCsvEmailUpload}
      />
    </>
  );
}
